/* Tonic theme scripts */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const theme = window.theme || { routes: {}, strings: {} };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Money
     --------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const fmt = format || theme.moneyFormat || '${{amount}}';
    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt;
    const styles = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.']
    };
    const [precision, thousands, decimal] = styles[match[1]] || styles.amount;
    const [whole, fraction] = (value / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fmt.replace(match[0], fraction ? grouped + decimal + fraction : grouped);
  }

  /* ---------------------------------------------------------------------
     Images: fade in once loaded
     --------------------------------------------------------------------- */
  const fadeSelector = '.card__media img, .goal__media img, .split__media img, .hero__media img, .gallery__slide img, .article-card__media img, .mega__promo-media img';
  if (document.body.classList.contains('has-animations') && !reducedMotion) {
    document.documentElement.classList.add('fade-ready');
  }
  function initImages(root = document) {
    $$(fadeSelector, root).forEach((img) => {
      if (img.complete) {
        img.classList.add('is-loaded');
      } else {
        const done = () => img.classList.add('is-loaded');
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }

  /* ---------------------------------------------------------------------
     Drawers (menu, search, cart, filters)
     --------------------------------------------------------------------- */
  let activeDrawer = null;
  let lastFocused = null;

  // Move drawers to <body> so sticky headers can't trap them under the overlay.
  function hoistDrawers(root = document) {
    $$('[data-drawer]', root).forEach((drawer) => {
      if (drawer.parentElement === document.body) return;
      const section = drawer.closest('.shopify-section');
      const existing = $$(`body > #${CSS.escape(drawer.id)}`).filter((el) => el !== drawer);
      existing.forEach((el) => el.remove());
      if (section) drawer.dataset.ownerSection = section.id;
      document.body.appendChild(drawer);
    });
  }

  function openDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return false;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(false);
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    $('[data-overlay]')?.classList.add('is-visible');
    document.body.classList.add('is-locked');
    $$(`[aria-controls="${id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
    activeDrawer = drawer;
    const focusTarget = $('[data-predictive-input]', drawer) || $('[data-drawer-close]', drawer);
    window.setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 80);
    return true;
  }

  function closeDrawer(restoreFocus = true) {
    if (!activeDrawer) return;
    const id = activeDrawer.id;
    activeDrawer.classList.remove('is-open');
    activeDrawer.setAttribute('aria-hidden', 'true');
    $$(`[aria-controls="${id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
    $('[data-overlay]')?.classList.remove('is-visible');
    document.body.classList.remove('is-locked');
    activeDrawer = null;
    if (restoreFocus && lastFocused && document.contains(lastFocused)) lastFocused.focus({ preventScroll: true });
  }

  document.addEventListener('keydown', (event) => {
    if (!activeDrawer) return;
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])', activeDrawer)
      .filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
    });
  }

  function renderCartDrawer(sections) {
    const html = sections && sections['cart-drawer'];
    const current = document.getElementById('cart-drawer');
    if (!html || !current) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.getElementById('cart-drawer');
    if (!fresh) return;
    current.innerHTML = fresh.innerHTML;
    initImages(current);
  }

  function setButtonLabel(button, text) {
    const label = $('[data-add-label]', button) || $('span', button) || button;
    label.textContent = text;
  }

  async function addToCart(form, submitter) {
    const button = submitter || $('[type="submit"]', form);
    const errorBox = $('[data-form-error]', form);
    const drawer = document.getElementById('cart-drawer');
    const originalLabel = button ? ($('[data-add-label]', button) || $('span', button) || button).textContent : '';

    if (errorBox) errorBox.hidden = true;
    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      setButtonLabel(button, theme.strings.adding || 'Adding…');
    }

    const formData = new FormData(form);
    if (drawer) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const message = data.description || data.message || theme.strings.error;
        if (errorBox) {
          errorBox.textContent = message;
          errorBox.hidden = false;
        } else {
          window.alert(message);
        }
        return;
      }
      renderCartDrawer(data.sections);
      const cart = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      updateCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('tonic:cart-updated', { detail: { cart } }));
      if (!openDrawer('cart-drawer')) window.location.href = theme.routes.cart;
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = theme.strings.error;
        errorBox.hidden = false;
      }
    } finally {
      if (button) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        setButtonLabel(button, originalLabel);
      }
    }
  }

  async function changeCartLine(line, quantity) {
    const drawer = document.getElementById('cart-drawer');
    drawer?.classList.add('is-loading');
    try {
      const response = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: Math.max(0, Number(quantity)),
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (data.sections) renderCartDrawer(data.sections);
      if (typeof data.item_count === 'number') updateCartCount(data.item_count);
      if (!response.ok && data.description) window.alert(data.description);
    } catch (error) {
      window.location.href = theme.routes.cart;
    } finally {
      document.getElementById('cart-drawer')?.classList.remove('is-loading');
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('form[data-product-form]') && theme.cartType === 'drawer' && document.getElementById('cart-drawer')) {
      event.preventDefault();
      addToCart(form, event.submitter);
      return;
    }
    if (form.matches('[data-filter-form]')) {
      event.preventDefault();
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      const query = params.toString();
      window.location.href = `${form.getAttribute('action')}${query ? `?${query}` : ''}`;
      return;
    }
    if (form.matches('[data-confirm]') && !window.confirm(form.dataset.confirm)) {
      event.preventDefault();
    }
  });

  /* ---------------------------------------------------------------------
     Delegated clicks
     --------------------------------------------------------------------- */
  const hoverNone = window.matchMedia('(hover: none)');

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      if (openDrawer(opener.dataset.drawerOpen)) event.preventDefault();
      return;
    }

    if (event.target.closest('[data-drawer-close]') || event.target.matches('[data-overlay]')) {
      closeDrawer();
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      changeCartLine(cartChange.dataset.line, cartChange.dataset.quantity);
      return;
    }

    const qtyButton = event.target.closest('[data-qty-minus], [data-qty-plus]');
    if (qtyButton) {
      const input = $('input', qtyButton.closest('[data-qty]'));
      if (!input) return;
      const min = input.min !== '' ? Number(input.min) : 0;
      const step = qtyButton.matches('[data-qty-plus]') ? 1 : -1;
      input.value = Math.max(min, (Number(input.value) || 0) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // Touch devices: first tap on a parent menu item opens its dropdown
    const menuLink = event.target.closest('.menu__item.has-children > .menu__link');
    if (menuLink && hoverNone.matches) {
      const item = menuLink.parentElement;
      if (!item.classList.contains('is-open')) {
        event.preventDefault();
        $$('.menu__item.is-open').forEach((el) => el.classList.remove('is-open'));
        item.classList.add('is-open');
      }
      return;
    }
    if (!event.target.closest('.menu__item')) {
      $$('.menu__item.is-open').forEach((el) => el.classList.remove('is-open'));
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches('[data-cart-page-qty]')) {
      target.form?.submit();
    } else if (target.matches('.toolbar select[data-filter-input]')) {
      target.form?.requestSubmit ? target.form.requestSubmit() : target.form?.submit();
    } else if (target.matches('[data-localization-select]')) {
      target.form?.submit();
    }
  });

  /* ---------------------------------------------------------------------
     Slider (hero, product rows, reviews, product gallery)
     --------------------------------------------------------------------- */
  function initSlider(root) {
    if (root._slider) return root._slider;
    const track = $('[data-slider-track]', root);
    if (!track) return null;

    const slides = () => $$('[data-slide]', track);
    const prev = $('[data-slider-prev]', root);
    const next = $('[data-slider-next]', root);
    const dots = $$('[data-slider-dot]', root);
    let index = 0;
    let timer = null;
    let ticking = false;

    const scrollable = () => track.scrollWidth > track.clientWidth + 2;
    const perView = () => {
      const first = slides()[0];
      return first ? Math.max(1, Math.round(track.clientWidth / first.getBoundingClientRect().width)) : 1;
    };

    function setActive(i) {
      index = i;
      dots.forEach((dot, n) => {
        const active = n === i;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const activeDot = dots[i];
      const dotsWrap = activeDot?.parentElement;
      if (activeDot && dotsWrap && dotsWrap.scrollWidth > dotsWrap.clientWidth) {
        dotsWrap.scrollTo({ left: activeDot.offsetLeft - dotsWrap.clientWidth / 2 + activeDot.clientWidth / 2, behavior: 'smooth' });
      }
      updateButtons();
    }

    function updateButtons() {
      const canScroll = scrollable();
      root.classList.toggle('is-static', !canScroll);
      if (prev) prev.disabled = !canScroll || track.scrollLeft <= 2;
      if (next) next.disabled = !canScroll || track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }

    function goTo(i, smooth = true) {
      const list = slides();
      if (!list.length) return;
      const target = Math.max(0, Math.min(i, list.length - 1));
      if (scrollable()) {
        track.scrollTo({ left: list[target].offsetLeft - list[0].offsetLeft, behavior: smooth && !reducedMotion ? 'smooth' : 'auto' });
      }
      setActive(target);
    }

    function currentIndex() {
      const list = slides();
      if (!list.length) return 0;
      const origin = list[0].offsetLeft;
      let best = 0;
      let bestDistance = Infinity;
      list.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - origin - track.scrollLeft);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setActive(currentIndex());
        ticking = false;
      });
    }, { passive: true });

    prev?.addEventListener('click', () => { pause(); goTo(currentIndex() - perView()); });
    next?.addEventListener('click', () => { pause(); goTo(currentIndex() + perView()); });
    dots.forEach((dot) => dot.addEventListener('click', () => { pause(); goTo(Number(dot.dataset.sliderDot)); }));

    function play() {
      const delay = Number(root.dataset.autoplay);
      if (!delay || reducedMotion || timer) return;
      timer = window.setInterval(() => {
        const list = slides();
        goTo(index + 1 >= list.length ? 0 : index + 1);
      }, delay);
    }
    function pause() {
      window.clearInterval(timer);
      timer = null;
    }

    if (root.dataset.autoplay) {
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', play);
      root.addEventListener('focusin', pause);
      play();
    }

    window.addEventListener('resize', updateButtons, { passive: true });
    const start = Number(root.dataset.start || 0);
    if (start > 0) window.requestAnimationFrame(() => goTo(start, false));
    updateButtons();

    root._slider = { goTo, pause, play, destroy: pause };
    return root._slider;
  }

  /* ---------------------------------------------------------------------
     Announcement rotator
     --------------------------------------------------------------------- */
  function initRotator(root) {
    if (root._rotator) return;
    const items = $$('[data-rotator-item]', root);
    if (items.length < 2) return;
    let i = 0;
    const show = (n) => {
      i = n;
      items.forEach((item, k) => item.classList.toggle('is-active', k === n));
    };
    let timer = window.setInterval(() => show((i + 1) % items.length), Number(root.dataset.interval) || 5000);
    root.addEventListener('mouseenter', () => window.clearInterval(timer));
    root.addEventListener('mouseleave', () => {
      window.clearInterval(timer);
      timer = window.setInterval(() => show((i + 1) % items.length), Number(root.dataset.interval) || 5000);
    });
    root._rotator = { show, stop: () => window.clearInterval(timer) };
  }

  /* ---------------------------------------------------------------------
     Product page: variants, subscriptions, sticky bar
     --------------------------------------------------------------------- */
  function initProduct(section) {
    if (section._product) return;
    const jsonEl = $('[data-product-json]', section);
    if (!jsonEl) return;
    section._product = true;

    const product = JSON.parse(jsonEl.textContent);
    const form = $('form[data-product-form]', section);
    const idInput = form ? $('[data-variant-id]', form) : null;
    const planInput = form ? $('[data-selling-plan-input]', form) : null;
    const planRadios = $$('[data-plan-radio]', section);
    const gallery = $('[data-gallery]', section);
    const optionCount = product.options.length;

    const currentVariant = () =>
      product.variants.find((v) => String(v.id) === String(idInput?.value)) || product.variants[0];
    const selectedPlanId = () => (planRadios.find((r) => r.checked) || {}).value || '';

    function selectedOptions() {
      const values = [];
      for (let position = 1; position <= optionCount; position += 1) {
        const checked = $(`input[data-option-position="${position}"]:checked`, section);
        const select = $(`select[data-option-position="${position}"]`, section);
        values.push(checked ? checked.value : select ? select.value : null);
      }
      return values;
    }

    function priceMarkup(variant) {
      const planId = selectedPlanId();
      const allocation = planId
        ? (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === planId)
        : null;
      const price = allocation ? allocation.price : variant.price;
      let compare = allocation ? allocation.compare_at_price : variant.compare_at_price;
      if (!compare || compare <= price) compare = null;
      let unit = '';
      if (variant.unit_price_measurement && variant.unit_price) {
        const m = variant.unit_price_measurement;
        unit = `<span class="price__unit">${formatMoney(variant.unit_price)}/${m.reference_value !== 1 ? m.reference_value : ''}${m.reference_unit}</span>`;
      }
      return `<div class="price${compare ? ' price--sale' : ''}" data-price><span class="price__current">${formatMoney(price)}</span>${
        compare ? `<s class="price__compare"><span class="visually-hidden">Regular price</span>${formatMoney(compare)}</s>` : ''
      }${unit}</div>`;
    }

    function renderPrices(variant) {
      const markup = priceMarkup(variant);
      $$('[data-product-price], [data-sticky-price]', section).forEach((el) => { el.innerHTML = markup; });
      $$('[data-plan-price]', section).forEach((el) => {
        const planId = el.dataset.planPrice;
        const allocation = planId
          ? (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === planId)
          : null;
        el.innerHTML = formatMoney(allocation ? allocation.price : variant.price);
      });
    }

    function updateAvailability(selected) {
      for (let i = 0; i < optionCount; i += 1) {
        $$(`input[data-option-position="${i + 1}"]`, section).forEach((input) => {
          const test = selected.slice();
          test[i] = input.value;
          const available = product.variants.some((v) => v.available && v.options.every((o, j) => o === test[j]));
          input.nextElementSibling?.classList.toggle('is-unavailable', !available);
        });
      }
    }

    function setButtons(disabled, text) {
      $$('[data-add-button], [data-sticky-button]', section).forEach((button) => {
        button.disabled = disabled;
        setButtonLabel(button, text);
      });
    }

    function onOptionChange() {
      const selected = selectedOptions();
      selected.forEach((value, i) => {
        const label = $(`[data-option-label="${i + 1}"]`, section);
        if (label) label.textContent = value;
      });
      updateAvailability(selected);

      const variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      if (!variant) {
        setButtons(true, theme.strings.unavailable || 'Unavailable');
        return;
      }
      if (idInput) idInput.value = variant.id;
      setButtons(!variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      renderPrices(variant);

      if (variant.featured_media && gallery) {
        const slider = initSlider(gallery);
        const index = $$('[data-slide]', gallery).findIndex((s) => s.dataset.mediaId === String(variant.featured_media.id));
        if (slider && index > -1) slider.goTo(index);
      }

      if (document.body.classList.contains('template-product') && section.dataset.productUrl) {
        window.history.replaceState({}, '', `${section.dataset.productUrl}?variant=${variant.id}`);
      }
      section.dispatchEvent(new CustomEvent('tonic:variant-change', { bubbles: true, detail: { variant } }));
    }

    function onPlanChange() {
      const planId = selectedPlanId();
      if (planInput) {
        planInput.value = planId;
        planInput.disabled = !planId;
      }
      renderPrices(currentVariant());
    }

    section.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-position]')) onOptionChange();
      if (event.target.matches('[data-plan-radio]')) onPlanChange();
    });
    if (planRadios.length) onPlanChange();

    const sticky = $('[data-sticky-atc]', section);
    const buyBlock = $('[data-buy-buttons]', section);
    if (sticky && buyBlock && 'IntersectionObserver' in window) {
      const stickyButton = $('[data-sticky-button]', sticky);
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        if (stickyButton) stickyButton.tabIndex = show ? 0 : -1;
      }).observe(buyBlock);
    }
  }

  /* ---------------------------------------------------------------------
     Predictive search
     --------------------------------------------------------------------- */
  function initPredictiveSearch(root = document) {
    $$('[data-predictive-input]', root).forEach((input) => {
      if (input._predictive) return;
      input._predictive = true;
      const container = input.closest('.search-drawer__inner');
      const results = container ? $('[data-predictive-results]', container) : null;
      if (!results || !theme.routes.predictiveSearch) return;
      let timer;
      let controller;

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        const query = input.value.trim();
        if (!query) {
          results.innerHTML = '';
          return;
        }
        timer = window.setTimeout(async () => {
          controller?.abort();
          controller = new AbortController();
          const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article,query&resources[limit]=6&section_id=predictive-search`;
          try {
            const response = await fetch(url, { signal: controller.signal });
            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const section = doc.getElementById('shopify-section-predictive-search');
            results.innerHTML = section ? section.innerHTML : '';
            initImages(results);
          } catch (error) {
            if (error.name !== 'AbortError') results.innerHTML = '';
          }
        }, 250);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Product recommendations
     --------------------------------------------------------------------- */
  function initRecommendations(root = document) {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (el._loaded || !el.dataset.url) return;
      el._loaded = true;
      try {
        const response = await fetch(el.dataset.url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.querySelector('.card')) {
          el.innerHTML = fresh.innerHTML;
          initImages(el);
        } else {
          el.hidden = true;
        }
      } catch (error) {
        el.hidden = true;
      }
    });
  }

  /* ---------------------------------------------------------------------
     Customer address forms: country → province
     --------------------------------------------------------------------- */
  function initAddressForms(root = document) {
    $$('[data-country-select]', root).forEach((country) => {
      if (country._address) return;
      country._address = true;
      const wrap = country.closest('[data-address-country]');
      const province = wrap ? $('[data-province-select]', wrap) : null;
      const provinceWrap = wrap ? $('[data-province-wrap]', wrap) : null;
      if (!province) return;
      if (country.dataset.default) country.value = country.dataset.default;

      const update = () => {
        const option = country.options[country.selectedIndex];
        let provinces = [];
        try {
          provinces = JSON.parse(option?.dataset.provinces || '[]');
        } catch (error) {
          provinces = [];
        }
        province.innerHTML = '';
        provinces.forEach(([value, label]) => province.add(new Option(label, value)));
        if (provinceWrap) provinceWrap.hidden = provinces.length === 0;
        if (province.dataset.default) province.value = province.dataset.default;
      };
      country.addEventListener('change', () => {
        province.dataset.default = '';
        update();
      });
      update();
    });
  }

  /* ---------------------------------------------------------------------
     Init + theme editor support
     --------------------------------------------------------------------- */
  function initAll(root = document) {
    hoistDrawers(root);
    initImages(root);
    $$('[data-slider]', root).forEach(initSlider);
    $$('[data-rotator]', root).forEach(initRotator);
    $$('[data-product]', root).forEach(initProduct);
    initPredictiveSearch(document);
    initRecommendations(root);
    initAddressForms(root);
  }

  initAll(document);

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    closeDrawer(false);
    $$('[data-slider]', event.target).forEach((el) => el._slider?.destroy());
    $$('[data-rotator]', event.target).forEach((el) => el._rotator?.stop());
    $$(`[data-owner-section="${event.target.id}"]`).forEach((el) => el.remove());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slider = event.target.closest('[data-slider]');
    if (slider) {
      const api = initSlider(slider);
      const index = $$('[data-slide]', slider).indexOf(event.target);
      if (api && index > -1) {
        api.pause();
        api.goTo(index);
      }
    }
    const rotator = event.target.closest('[data-rotator]');
    if (rotator && rotator._rotator) {
      rotator._rotator.stop();
      rotator._rotator.show($$('[data-rotator-item]', rotator).indexOf(event.target));
    }
  });

  window.Tonic = { formatMoney, openDrawer, closeDrawer, addToCart, changeCartLine };
})();
